import { useState } from 'react';
import { useEmailStore } from '../../../stores/emailStore';
import { Nda } from './Nda';
import { OncallNoticeWindow } from './OncallNoticeWindow';
import { playSound } from '../../../utils/soundService';

const getPreview = (body?: string, length: number = 200) => {
  if (!body) return '';
  return (
    body.split('\n')[0].substring(0, length) +
    (body.length > length ? '...' : '')
  );
};

export function EmailApp() {
  const [showOncallWindow, setShowOncallWindow] = useState(false);
  const emails = useEmailStore((state) => state.emails);
  const selectedEmailId = useEmailStore((state) => state.selectedEmailId);
  const selectEmail = useEmailStore((state) => state.selectEmail);

  const selectedEmail = emails.find((email) => email.id === selectedEmailId);

  return (
    <div className="h-full flex bg-white">
      {/* Main content */}
      <div className="flex-1 flex">
        {/* Email list */}
        <div className="w-96 border-r border-gray-200 flex flex-col overflow-hidden">
          {/* Header */}
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-sm font-medium text-gray-900">Inbox</h2>
            <p className="text-xs text-gray-600 mt-1">
              {emails.length} messages
            </p>
          </div>

          {/* Emails list */}
          <div className="flex-1 overflow-y-auto">
            {emails.map((email) => (
              <button
                key={email.id}
                onClick={() => {
                  playSound('select');
                  selectEmail(email.id);
                }}
                className={`w-full text-left px-4 py-3 border-b border-gray-200 hover:bg-gray-50 transition-colors ${
                  selectedEmail?.id === email.id ? 'bg-gray-100' : ''
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <p
                      className={`text-sm ${!email.read ? 'font-semibold text-gray-900' : 'font-medium text-gray-700'}`}
                    >
                      {email.from}
                    </p>
                    <p className="text-xs text-gray-600 mt-0.5 truncate">
                      {email.subject}
                    </p>
                    <p className="text-xs text-gray-500 mt-1 truncate">
                      {getPreview(email.body)}
                    </p>
                  </div>
                  {!email.read && (
                    <div className="w-2 h-2 rounded-full bg-gray-900 flex-shrink-0 mt-1" />
                  )}
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Email viewer */}
        {selectedEmail && (
          <div className="flex-1 flex flex-col overflow-hidden">
            {/* Email header */}
            <div className="border-b border-gray-200 p-6">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div className="flex-1">
                  <h1 className="text-lg font-medium text-gray-900">
                    {selectedEmail.subject}
                  </h1>
                  <p className="text-sm text-gray-600 mt-1">
                    From: {selectedEmail.from}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {selectedEmail.date}
                  </p>
                </div>
              </div>
            </div>

            {/* Email body */}
            {selectedEmail.id === '1' ? (
              <Nda />
            ) : (
              <div className="flex-1 overflow-y-auto p-6 flex flex-col">
                <div className="text-sm leading-relaxed text-gray-700 whitespace-pre-wrap">
                  {selectedEmail.body}
                </div>

                {/* Attachments section */}
                {selectedEmail.attachments &&
                  selectedEmail.attachments.length > 0 && (
                    <div className="mt-6 pt-6 border-t border-gray-200">
                      <p className="text-xs font-medium text-gray-600 mb-3">
                        Attachments ({selectedEmail.attachments.length})
                      </p>
                      <div className="space-y-2">
                        {selectedEmail.attachments.map((attachment) => (
                          <button
                            key={attachment.id}
                            onClick={() => {
                              playSound('open');
                              if (attachment.id === 'attach-1') {
                                setShowOncallWindow(true);
                              }
                            }}
                            className="w-full text-left flex items-center gap-2 p-2 rounded border border-gray-200 hover:bg-gray-50 cursor-pointer text-sm"
                          >
                            <svg
                              className="w-4 h-4 text-gray-600 flex-shrink-0"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                              />
                            </svg>
                            <div className="flex-1 min-w-0">
                              <p className="text-gray-900 truncate">
                                {attachment.name}
                              </p>
                              {attachment.size && (
                                <p className="text-xs text-gray-500">
                                  {(attachment.size / 1024).toFixed(1)} KB
                                </p>
                              )}
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
              </div>
            )}
          </div>
        )}
      </div>

      {showOncallWindow && (
        <OncallNoticeWindow onClose={() => setShowOncallWindow(false)} />
      )}
    </div>
  );
}
